import { config, SCOPE_CATEGORY, YEAR_OPTIONS } from "@/lib/constants";
import { onboardingService } from "../services";
const padToTwoDigits = (value) => String(value).padStart(2, "0");

const toIsoDateString = (date) =>
  `${date.getFullYear()}-${padToTwoDigits(date.getMonth() + 1)}-${padToTwoDigits(date.getDate())}`;

const parseDateOnlyToLocalDate = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const addDaysToIsoDate = (iso, days) => {
  const date = parseDateOnlyToLocalDate(iso);
  if (!date) return "";
  date.setDate(date.getDate() + days);
  return toIsoDateString(date);
};

export const addYearsMinusOneDay = (iso, years) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  date.setFullYear(date.getFullYear() + years);
  date.setDate(date.getDate() - 1);
  return toIsoDateString(date);
};

export const toDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

export const normalizeNxmapFormValue = (key, value) => {
  if (value === null || typeof value === "undefined") return "";
  if (
    key === "baseYearStart" ||
    key === "baseYearEnd" ||
    key === "reportingPeriodStart" ||
    key === "reportingPeriodEnd"
  )
    return toDateInput(value);
  if (key === "baseYear" || key === "threshold" || key === "reportingYear")
    return String(value);
  return value;
};

export const getBaseYearStartDate = (baseYear) => {
  if (!baseYear) return "";
  return `${baseYear}-01-01`;
};

export const getBaseYearDateRange = (baseYear) => {
  if (!baseYear) return { start: "", end: "" };
  return {
    start: `${baseYear}-01-01`,
    end: `${baseYear}-12-31`,
  };
};

export const getBaseYearStartBounds = (baseYear) => {
  const { start, end } = getBaseYearDateRange(baseYear);
  if (!start || !end) return { min: "", max: "" };
  return {
    min: start,
    max: addDaysToIsoDate(end, -1),
  };
};

export const getDefaultBaseYearDates = (baseYear) =>
  getBaseYearDateRange(baseYear);

export const getBaseYearEndBounds = (baseYear, baseYearStart) => {
  const { start, end } = getBaseYearDateRange(baseYear);
  if (!start || !end) return { min: "", max: "" };

  const normalizedStart = toDateInput(baseYearStart) || start;
  const min = addDaysToIsoDate(normalizedStart, 1);

  if (!min || min > end) {
    return { min: "", max: end };
  }

  return { min, max: end };
};

export const getReportYearStartDate = (reportingYear) => {
  if (!reportingYear) return "";
  return `${reportingYear}-01-01`;
};

export const getReportYearDateRange = (reportingYear) => {
  if (!reportingYear) return { start: "", end: "" };
  return {
    start: `${reportingYear}-01-01`,
    end: `${reportingYear}-12-31`,
  };
};

export const getReportYearStartBounds = (reportingYear) => {
  const { start, end } = getReportYearDateRange(reportingYear);
  if (!start || !end) return { min: "", max: "" };
  return {
    min: start,
    max: addDaysToIsoDate(end, -1),
  };
};

export const getDefaultReportYearDates = (reportingYear) =>
  getReportYearDateRange(reportingYear);

export const getReportYearEndBounds = (reportingYear, reportingPeriodStart) => {
  const { start, end } = getReportYearDateRange(reportingYear);
  if (!start || !end) return { min: "", max: "" };

  const normalizedStart = toDateInput(reportingPeriodStart) || start;
  const min = addDaysToIsoDate(normalizedStart, 1);

  if (!min || min > end) {
    return { min: "", max: end };
  }

  return { min, max: end };
};

const formatDateDisplay = (value) => {
  if (!value) return "";
  const date = parseDateOnlyToLocalDate(value) || new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const getBaseYearStartHelperText = (baseYear) => {
  if (!baseYear) return "Select a base year first.";
  const { start } = getDefaultBaseYearDates(baseYear);
  return `Default start date is ${formatDateDisplay(start)}.`;
};

export const getBaseYearEndHelperText = (bounds, baseYear) => {
  if (!baseYear) {
    return "Select a base year first.";
  }
  if (!bounds?.min || !bounds?.max) {
    return "Select an end date later than the start date.";
  }
  if (bounds.min === bounds.max) {
    return `Select an end date ${formatDateDisplay(bounds.min)}.`;
  }
  return `Select an end date between ${formatDateDisplay(bounds.min)} and ${formatDateDisplay(bounds.max)}. Dates on or before the selected start date are unavailable.`;
};

export const getReportYearStartHelperText = (reportingYear) => {
  if (!reportingYear) return "Select a report year first.";
  const { start } = getDefaultReportYearDates(reportingYear);
  return `Default start date is ${formatDateDisplay(start)}.`;
};

export const getReportYearEndHelperText = (bounds, reportingYear) => {
  if (!reportingYear) {
    return "Select a report year first.";
  }
  if (!bounds?.min || !bounds?.max) {
    return "Select an end date later than the start date.";
  }
  if (bounds.min === bounds.max) {
    return `Select an end date ${formatDateDisplay(bounds.min)}.`;
  }
  return `Select an end date between ${formatDateDisplay(bounds.min)} and ${formatDateDisplay(bounds.max)}. Dates on or before the selected start date are unavailable.`;
};
export const getLabel = (key, list) => {
  if (!key) return "";
  return list?.find((l) => l.key === key)?.value;
};

export const toDateOnly = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toISOString().split("T")[0];
  } catch {
    return dateStr;
  }
};

export const normalizeNxmapDataForProgress = (
  data = {},
  baseYear,
  organizationBoundary,
  reportingYear,
  reportingStandard,
) => {
  const defaultRowDetail = {
    reqByLaw: true,
    dataCredibility: 1,
    emissionFactorSource: 1,
    technologyFeasibility: 1,
    occurrenceFrequency: 1,
    emissionVolume: 1,
    targetYear: YEAR_OPTIONS[0]?.key || "",
  };

  const materialityAssessment = data.materialityAssessment;
  const fromArray = Array.isArray(materialityAssessment)
    ? materialityAssessment.reduce((acc, item) => {
        if (item?.categoryKey) acc[item.categoryKey] = item;
        return acc;
      }, {})
    : {};

  const rowDetails = (
    Array.isArray(SCOPE_CATEGORY) ? SCOPE_CATEGORY : []
  ).reduce((acc, category) => {
    const saved =
      (materialityAssessment && materialityAssessment[category.key]) ||
      fromArray[category.key];
    acc[category.key] = saved
      ? { ...defaultRowDetail, ...saved }
      : { ...defaultRowDetail };
    return acc;
  }, {});

  const form = {
    organizationBoundary:
      organizationBoundary ?? data.organizationBoundary ?? "",
    ghgReportPurpose: data.ghgReportPurpose ?? "",
    reportingStandard: reportingStandard ?? data.reportingStandard ?? "",
    baseYear: normalizeNxmapFormValue("baseYear", baseYear ?? data.baseYear),
    baseYearStart: normalizeNxmapFormValue("baseYearStart", data.baseYearStart),
    baseYearEnd: normalizeNxmapFormValue("baseYearEnd", data.baseYearEnd),
    threshold: normalizeNxmapFormValue("threshold", data.threshold),
    reportingYear: normalizeNxmapFormValue(
      "reportingYear",
      reportingYear ?? data.reportingYear,
    ),
    reportingPeriodStart: normalizeNxmapFormValue(
      "reportingPeriodStart",
      data.reportingPeriodStart,
    ),
    reportingPeriodEnd: normalizeNxmapFormValue(
      "reportingPeriodEnd",
      data.reportingPeriodEnd,
    ),
  };

  return {
    form,
    rowDetails,
    materialityAssessment,
  };
};

// export const visitProject = (projectName = null) => {
//   const redirectPath = "/"
//   const ssoVisitUrl = `${config.apiUrl}/visit/${projectName}?redirect=${encodeURIComponent(redirectPath)}`;
//   window.open(ssoVisitUrl, "_blank", "noopener,noreferrer");
//   return;
// };
export const visitProject = async (projectName = null) => {
  if (projectName == "nxplan") {
    // must be a top-level navigation: the SSO redirect chain crosses origins
    // and sets the nxplan session cookie, which XHR cannot do
    const ssoVisitUrl = `${config.apiUrl}/visit/nxplan?redirect=${encodeURIComponent("/")}`;
    window.open(ssoVisitUrl, "_blank", "noopener,noreferrer");
    return;
  }
  if (projectName == "nxmap") {
    const response = await onboardingService.getNxmapCode({
      showErrorToast: false,
    });
    const code = String(response || "").trim();
    if (!code) {
      throw new Error("Unable to retrieve NXMap access code.");
    }
    const base = String(config.nxmapBaseUrl || "").replace(/\/$/, "");
    const ssoUrl = `${base}/sso-login?code=${encodeURIComponent(code)}`;
    window.open(ssoUrl, "_blank", "noopener,noreferrer");
  }
};

export const GHG_REPORT_PURPOSE = [
  { key: "compliance", value: "Compliance" },
  { key: "internal_reporting", value: "Internal Reporting" },
  { key: "stakeholders_requirement", value: "Stakeholders' Requirement" },
];
